/**
 * Ingredient Synonym Database
 *
 * Maps different names for the same ingredient to a canonical form.
 * Helps match ingredients across different recipe sources and languages.
 */

/**
 * Synonym map: canonical name → array of synonyms
 * The canonical name should be the most common/standard term
 */
export const INGREDIENT_SYNONYMS: Record<string, string[]> = {
  // Vegetables
  'green onion': ['scallion', 'scallions', 'spring onion', 'spring onions', 'salad onion'],
  'bell pepper': ['capsicum', 'sweet pepper', 'sweet peppers'],
  'eggplant': ['aubergine'],
  'zucchini': ['courgette', 'courgettes'],
  'arugula': ['rocket', 'roquette', 'rucola'],
  'snow pea': ['sugar snap pea', 'sugar snap peas', 'mangetout', 'mange-tout'],
  'corn': ['maize', 'sweetcorn', 'sweet corn'],
  'rutabaga': ['swede', 'yellow turnip'],

  // Herbs & Spices
  'cilantro': ['coriander leaves', 'chinese parsley', 'fresh coriander'],
  'coriander': ['coriander seed', 'coriander seeds'], // The seed form
  'basil': ['sweet basil'],
  'parsley': ['italian parsley', 'flat-leaf parsley'],
  'scallion': ['green onion'], // Reverse synonym

  // Proteins
  'shrimp': ['prawn', 'prawns'],
  'ground beef': ['minced beef', 'beef mince', 'hamburger meat'],
  'ground pork': ['minced pork', 'pork mince'],
  'ground turkey': ['minced turkey', 'turkey mince'],
  'chicken breast': ['chicken fillet', 'chicken fillets'],

  // Legumes
  'chickpea': ['garbanzo bean', 'garbanzo beans', 'ceci bean', 'ceci beans', 'chick pea'],
  'fava bean': ['broad bean', 'broad beans'],
  'lima bean': ['butter bean', 'butter beans'],

  // Grains
  'oatmeal': ['oat', 'oats', 'rolled oats'],
  'bulgur': ['bulgar', 'bulgur wheat', 'cracked wheat'],
  'couscous': ['israeli couscous', 'pearl couscous'],

  // Dairy
  'heavy cream': ['whipping cream', 'double cream'],
  'light cream': ['single cream', 'coffee cream'],
  'half and half': ['half-and-half', 'half & half'],
  'sour cream': ['soured cream'],

  // Fruits
  'cantaloupe': ['rockmelon', 'muskmelon'],
  'mandarin': ['tangerine', 'clementine'],

  // Pantry
  'baking soda': ['bicarbonate of soda', 'sodium bicarbonate'],
  'confectioners sugar': ['powdered sugar', 'icing sugar', 'confectioner sugar'],
  'all-purpose flour': ['plain flour', 'all purpose flour'],
  'cornstarch': ['corn starch', 'cornflour'],
  'molasses': ['treacle', 'black treacle'],
  'peanut': ['groundnut', 'ground nut'],

  // Condiments & Sauces
  'ketchup': ['catsup', 'tomato ketchup', 'tomato sauce'],
  'mayo': ['mayonnaise'],
  'soy sauce': ['soya sauce', 'shoyu'],

  // Oils & Fats
  'olive oil': ['extra virgin olive oil', 'evoo'],
  'vegetable oil': ['canola oil', 'rapeseed oil'],

  // Specialty Items
  'seltzer': ['sparkling water', 'carbonated water', 'club soda'],
  'graham cracker': ['digestive biscuit', 'digestive biscuits'],
};

/**
 * Reverse lookup map: synonym → canonical name
 * Built dynamically for fast lookups
 */
let REVERSE_SYNONYM_MAP: Record<string, string> | null = null;

function buildReverseSynonymMap(): Record<string, string> {
  if (REVERSE_SYNONYM_MAP) {
    return REVERSE_SYNONYM_MAP;
  }

  const reverseMap: Record<string, string> = {};

  for (const [canonical, synonyms] of Object.entries(INGREDIENT_SYNONYMS)) {
    // Map canonical name to itself
    reverseMap[canonical] = canonical;

    // Map each synonym to canonical
    for (const synonym of synonyms) {
      reverseMap[synonym.toLowerCase()] = canonical;
    }
  }

  REVERSE_SYNONYM_MAP = reverseMap;
  return reverseMap;
}

/**
 * Get the canonical form of an ingredient name
 *
 * @param name - Ingredient name (normalized)
 * @returns Canonical name if synonym found, otherwise original name
 *
 * @example
 * getCanonicalIngredient('scallion') // 'green onion'
 * getCanonicalIngredient('green onion') // 'green onion'
 * getCanonicalIngredient('cucumber') // 'cucumber' (not in database)
 */
export function getCanonicalIngredient(name: string): string {
  const normalized = name.toLowerCase().trim();
  const reverseMap = buildReverseSynonymMap();

  // Check if we have a synonym match
  const canonical = reverseMap[normalized];
  if (canonical) {
    return canonical;
  }

  // No synonym found, return original
  return normalized;
}

/**
 * Check if two ingredient names are synonyms
 *
 * @example
 * areSynonyms('scallion', 'green onion') // true
 * areSynonyms('cilantro', 'coriander leaves') // true
 * areSynonyms('tomato', 'cucumber') // false
 */
export function areSynonyms(name1: string, name2: string): boolean {
  const canonical1 = getCanonicalIngredient(name1);
  const canonical2 = getCanonicalIngredient(name2);

  return canonical1 === canonical2;
}

/**
 * Get all synonyms for an ingredient (including canonical name)
 *
 * @example
 * getAllSynonyms('scallion') // ['green onion', 'scallion', 'spring onion', ...]
 */
export function getAllSynonyms(name: string): string[] {
  const canonical = getCanonicalIngredient(name);

  // Return canonical + all its synonyms
  return [canonical, ...(INGREDIENT_SYNONYMS[canonical] || [])];
}
